"use client";

import { useState } from "react";
import { Facebook, Twitter, Linkedin, Instagram, Link as LinkIcon, Check } from "lucide-react";

export default function ShareButtons({
      title,
      slug,
      image,
}: {
      title: string;
      slug: string;
      image?: string | null;
}) {
      const [copied, setCopied] = useState(false);
      const url = `https://wolisticcenter.com/blog/${slug}`;

      // URL ve Başlıkları encode ediyoruz (Linklerin bozulmaması için)
      const encodedUrl = encodeURIComponent(url);
      const encodedTitle = encodeURIComponent(title);

      const handleCopyLink = async () => {
            try {
                  // Modern kopyalama yöntemi
                  await navigator.clipboard.writeText(url);
                  setCopied(true);
            } catch (err) {
                  // Eğer clipboard API çalışmazsa eski yöntem (fallback)
                  const textArea = document.createElement("textarea");
                  textArea.value = url;
                  document.body.appendChild(textArea);
                  textArea.select();
                  try {
                        document.execCommand('copy');
                        setCopied(true);
                  } catch (e) {
                        console.error("Link kopyalanamadı:", e);
                  }
                  document.body.removeChild(textArea);
            }

            // 2 saniye sonra bildirim kaybolur
            setTimeout(() => setCopied(false), 2000);
      };

      const handleInstagramShare = async () => {
            try {
                  // 1. Önce blog URL'sini kopyalamayı dene
                  try {
                        await navigator.clipboard.writeText(url);
                        setCopied(true);
                  } catch (clipboardError) {
                        console.warn(
                              "Clipboard API çalışmadı, fallback deneniyor:",
                              clipboardError
                        );

                        // Clipboard fallback
                        const textArea = document.createElement("textarea");
                        textArea.value = url;

                        textArea.style.position = "fixed";
                        textArea.style.left = "-9999px";
                        textArea.style.top = "0";

                        document.body.appendChild(textArea);

                        textArea.focus();
                        textArea.select();

                        try {
                              document.execCommand("copy");
                              setCopied(true);
                        } catch (fallbackError) {
                              console.error(
                                    "URL kopyalanamadı:",
                                    fallbackError
                              );
                        }

                        document.body.removeChild(textArea);
                  }

                  // 2. Story görselini oluştur
                  const storyImageUrl =
                        `${window.location.origin}/api/instagram-story` +
                        `?title=${encodeURIComponent(title)}` +
                        `&url=${encodeURIComponent(url)}` +
                        `${image ? `&image=${encodeURIComponent(image)}` : ""}`;

                  const response = await fetch(storyImageUrl);

                  if (!response.ok) {
                        throw new Error(
                              "Story görseli oluşturulamadı."
                        );
                  }

                  const blob = await response.blob();

                  // 3. Dosya oluştur
                  const file = new File(
                        [blob],
                        "wolistic-center-story.png",
                        {
                              type: "image/png",
                        }
                  );

                  // 4. Telefonun paylaşım menüsünü aç
                  if (
                        navigator.share &&
                        navigator.canShare?.({
                              files: [file],
                        })
                  ) {
                        await navigator.share({
                              files: [file],
                              title: title,
                        });

                        return;
                  }

                  // 5. Paylaşım desteklenmiyorsa dosyayı indir
                  const imageUrl =
                        URL.createObjectURL(blob);

                  const a =
                        document.createElement("a");

                  a.href = imageUrl;
                  a.download =
                        "wolistic-center-story.png";

                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);

                  URL.revokeObjectURL(imageUrl);

            } catch (error) {
                  console.error(
                        "Instagram Story paylaşımı başarısız:",
                        error
                  );
            }
      };
      // WhatsApp için özel SVG İkonu
      const WhatsAppIcon = () => (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9L3 21" />
                  <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1Z" />
                  <path d="M14 14a.5.5 0 0 0 1 0v-1a.5.5 0 0 0-1 0v1Z" />
            </svg>
      );

      return (
            <div className="flex flex-wrap items-center justify-between gap-6 py-10 border-y border-gray-100 my-16">
                  <span className="text-base font-bold text-[#404041] uppercase tracking-widest">Bu Yazıyı Paylaş</span>

                  <div className="flex gap-3 sm:gap-4">
                        {/* Kopyalandı Bildirimi (Toast) - Artık ekranda sabit (fixed) ve her zaman üstte (z-50) */}
                        {copied && (
                              <div className="fixed bottom-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-sm px-6 py-3 rounded-full shadow-2xl flex items-center gap-2 animate-in fade-in slide-in-from-bottom-5 duration-300 whitespace-nowrap z-50">
                                    <Check size={16} className="text-green-400" />
                                    Story hazır — bağlantı kopyalandı!
                              </div>
                        )}

                        {/* WhatsApp */}
                        <a
                              href={`https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-12 h-12 rounded-full bg-white border border-gray-200 text-[#25D366] flex items-center justify-center hover:bg-[#25D366] hover:text-white hover:border-[#25D366] transition-all shadow-sm hover:shadow-lg"
                              title="WhatsApp'ta Paylaş"
                        >
                              <WhatsAppIcon />
                        </a>

                        {/* Facebook */}
                        <a
                              href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-12 h-12 rounded-full bg-white border border-gray-200 text-[#3b5998] flex items-center justify-center hover:bg-[#3b5998] hover:text-white hover:border-[#3b5998] transition-all shadow-sm hover:shadow-lg"
                              title="Facebook'ta Paylaş"
                        >
                              <Facebook size={20} />
                        </a>

                        {/* Twitter (X) */}
                        <a
                              href={`https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-12 h-12 rounded-full bg-white border border-gray-200 text-[#1DA1F2] flex items-center justify-center hover:bg-[#1DA1F2] hover:text-white hover:border-[#1DA1F2] transition-all shadow-sm hover:shadow-lg"
                              title="Twitter'da Paylaş"
                        >
                              <Twitter size={20} />
                        </a>

                        {/* LinkedIn */}
                        <a
                              href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-12 h-12 rounded-full bg-white border border-gray-200 text-[#0077b5] flex items-center justify-center hover:bg-[#0077b5] hover:text-white hover:border-[#0077b5] transition-all shadow-sm hover:shadow-lg"
                              title="LinkedIn'de Paylaş"
                        >
                              <Linkedin size={20} />
                        </a>

                        <button
                              onClick={handleInstagramShare}
                              type="button"
                              className="w-12 h-12 rounded-full bg-white border border-gray-200 text-[#E1306C] flex items-center justify-center hover:bg-[#E1306C] hover:text-white hover:border-[#E1306C] transition-all shadow-sm hover:shadow-lg"
                              title="Instagram'da Story olarak paylaş"
                        >
                              <Instagram size={20} />
                        </button>

                        {/* Linki Kopyala Butonu */}
                        <button
                              onClick={handleCopyLink}
                              className="w-12 h-12 rounded-full bg-white border border-gray-200 text-[#404041] flex items-center justify-center hover:bg-[#404041] hover:text-white hover:border-[#404041] transition-all shadow-sm hover:shadow-lg"
                              title="Bağlantıyı Kopyala"
                        >
                              <LinkIcon size={20} />
                        </button>
                  </div>
            </div>
      );
}